import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import AuthModal from "@/components/AuthModal";
import { Bell, Search, Lock, User as UserIcon, Shield, LogOut, Users } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  }, [user]);

  return (
    <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur border-b-2 border-ink">
      <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-1.5 group">
          <span className="w-7 h-7 bg-brand border-2 border-ink flex items-center justify-center shadow-brutal-sm">
            <Lock className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </span>
          <span className="font-display text-xl font-bold tracking-tight leading-none">{BRAND.name}</span>
        </Link>

        <div className="flex items-center gap-2">
          <Link to="/search" data-testid="nav-search" aria-label="Search"
            className="w-9 h-9 border-2 border-ink bg-white flex items-center justify-center btn-press shadow-brutal-sm">
            <Search className="w-4 h-4" strokeWidth={2.5} />
          </Link>
          {user && (
            <Link to="/notifications" data-testid="nav-notifications" aria-label="Notifications"
              className="relative w-9 h-9 border-2 border-ink bg-white flex items-center justify-center btn-press shadow-brutal-sm">
              <Bell className="w-4 h-4" strokeWidth={2.5} />
              {unread > 0 && (
                <span className="absolute -top-2 -right-2 min-w-5 h-5 px-1 bg-missed text-white text-xs font-bold border-2 border-ink rounded-full flex items-center justify-center">
                  {unread}
                </span>
              )}
            </Link>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-avatar" className="outline-none">
                <Avatar className="w-9 h-9 border-2 border-ink">
                  <AvatarImage src={user.avatar_url} />
                  <AvatarFallback className="bg-locked font-bold">
                    {(user.display_name || "U")[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-2 border-ink shadow-brutal w-52">
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/@${user.username}`)}>
                  <UserIcon className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-following" onClick={() => navigate("/following")}>
                  <Users className="w-4 h-4 mr-2" /> Following
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>
                  Settings
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Shield className="w-4 h-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button data-testid="nav-signin" onClick={() => setAuthOpen(true)}
              className="h-9 px-4 border-2 border-ink bg-ink text-white font-semibold btn-press shadow-brutal-sm text-sm">
              Sign in
            </button>
          )}
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}
